import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Check } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { portalPageStyles, portalContactStyles as styles } from '@/styles/portalPageStyles';

interface FormData {
  name: string;
  email: string;
  phone: string;
  schoolName: string;
  role: string;
  studentCount: string;
  message: string;
  companyUrl: string; // honeypot
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_OPTIONS = [
  '',
  'Administrator / Principal',
  'Teacher',
  'District Staff',
  'IT Director',
  'Other',
];

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

function ContactPage() {
  usePageTitle('Contact Us');
  const { isMobile } = useMediaQuery();
  const mountTime = useRef(Date.now());
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    schoolName: '',
    role: '',
    studentCount: '',
    message: '',
    companyUrl: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Load Turnstile script and render widget
  useEffect(() => {
    // Don't load if already submitted
    if (submitted) return;

    const renderWidget = () => {
      if (!turnstileRef.current || !window.turnstile || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
        theme: 'light',
        size: 'normal',
      });
    };

    // If Turnstile already loaded (e.g. hot reload), render immediately
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Check if script is already in the DOM
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [submitted]);

  const validate = useCallback((): FormErrors => {
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(form.email.trim())) {
      errs.email = 'Please enter a valid email address';
    }
    if (!form.message.trim()) {
      errs.message = 'Message is required';
    } else if (form.message.trim().length < 10) {
      errs.message = 'Message must be at least 10 characters';
    }
    return errs;
  }, [form.name, form.email, form.message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (form.companyUrl) return;

    // Timing check (< 3 seconds)
    if (Date.now() - mountTime.current < 3000) return;

    if (!turnstileToken) {
      setSubmitError('Please complete the verification');
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);
    setSubmitError(null);

    const trimmedFields = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      schoolName: form.schoolName.trim(),
      role: form.role,
      studentCount: form.studentCount.trim(),
      message: form.message.trim(),
    };

    try {
      const res = await fetch('/api/portal/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...trimmedFields, turnstileToken, companyUrl: form.companyUrl }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 429) throw new Error('Please wait a moment before trying again');
        throw new Error(data.error || 'Something went wrong');
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to send');
      if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
      setTurnstileToken('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const inputFocusStyle = (field: string): React.CSSProperties =>
    focusedField === field ? { borderColor: '#f38d4b' } : {};

  const errorId = (field: string) => `${field}-error`;

  const rowStyle = isMobile ? styles.fieldRowMobile : styles.fieldRow;

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav currentPage="contact" />

      {/* Hero */}
      <section style={styles.hero}>
        <h1 style={isMobile ? styles.heroTitleMobile : styles.heroTitle}>Get in Touch</h1>
        <p style={styles.heroSubtitle}>
          Learn how Checkademic can simplify behavior tracking at your school
        </p>
      </section>

      {/* Form */}
      <div style={portalPageStyles.container}>
        <div style={isMobile ? styles.formCardMobile : styles.formCard}>
          {submitted ? (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>
                <Check size={32} color="#22c55e" />
              </div>
              <div style={styles.successTitle}>Message sent!</div>
              <p style={styles.successText}>
                Thank you for reaching out. We'll get back to you within 1 business day.
              </p>
              <p style={styles.successText}>You'll receive a confirmation email shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <div style={styles.honeypot} aria-hidden="true">
                <label htmlFor="company_url">Company URL</label>
                <input
                  type="text"
                  id="company_url"
                  name="company_url"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.companyUrl}
                  onChange={(e) => handleChange('companyUrl', e.target.value)}
                />
              </div>

              {/* Row 1: Name + Email */}
              <div style={rowStyle}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-name">
                    Name <span style={{ color: 'var(--error-color)' }}>*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    style={{ ...styles.input, ...inputFocusStyle('name') }}
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    aria-required={true}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? errorId('name') : undefined}
                    placeholder="Your full name"
                  />
                  {errors.name && (
                    <p id={errorId('name')} style={styles.errorText}>
                      {errors.name}
                    </p>
                  )}
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-email">
                    Email <span style={{ color: 'var(--error-color)' }}>*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    style={{ ...styles.input, ...inputFocusStyle('email') }}
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    aria-required={true}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? errorId('email') : undefined}
                    placeholder="you@school.edu"
                  />
                  {errors.email && (
                    <p id={errorId('email')} style={styles.errorText}>
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Phone + School Name */}
              <div style={rowStyle}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-phone">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    style={{ ...styles.input, ...inputFocusStyle('phone') }}
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-school">
                    School Name
                  </label>
                  <input
                    id="contact-school"
                    type="text"
                    style={{ ...styles.input, ...inputFocusStyle('schoolName') }}
                    value={form.schoolName}
                    onChange={(e) => handleChange('schoolName', e.target.value)}
                    onFocus={() => setFocusedField('schoolName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Your school or district"
                  />
                </div>
              </div>

              {/* Row 3: Role + Student Count */}
              <div style={rowStyle}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-role">
                    Role
                  </label>
                  <select
                    id="contact-role"
                    style={{ ...styles.select, ...inputFocusStyle('role') }}
                    value={form.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    onFocus={() => setFocusedField('role')}
                    onBlur={() => setFocusedField(null)}
                  >
                    <option value="">Select your role</option>
                    {ROLE_OPTIONS.filter(Boolean).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="contact-students">
                    Student Count
                  </label>
                  <input
                    id="contact-students"
                    type="text"
                    style={{ ...styles.input, ...inputFocusStyle('studentCount') }}
                    value={form.studentCount}
                    onChange={(e) => handleChange('studentCount', e.target.value)}
                    onFocus={() => setFocusedField('studentCount')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="e.g. 200"
                  />
                </div>
              </div>

              {/* Row 4: Message */}
              <div style={styles.fullWidthGroup}>
                <label style={styles.label} htmlFor="contact-message">
                  Message <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                <textarea
                  id="contact-message"
                  style={{ ...styles.textarea, ...inputFocusStyle('message') }}
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  aria-required={true}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? errorId('message') : undefined}
                  placeholder="Tell us about your school and what you're looking for..."
                />
                {errors.message && (
                  <p id={errorId('message')} style={styles.errorText}>
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Turnstile Widget */}
              <div ref={turnstileRef} style={{ marginBottom: '1rem' }} />

              {submitError && (
                <p style={{ ...styles.errorText, marginBottom: '1rem' }}>{submitError}</p>
              )}

              <button type="submit" style={styles.submitButton} disabled={submitting}>
                <Send size={18} />
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>

        <p style={styles.belowFormText}>
          Prefer email? Reach us at{' '}
          <a href="mailto:sales@checkademic.com" style={{ color: '#f38d4b' }}>
            sales@checkademic.com
          </a>
        </p>
      </div>

      <PortalFooter currentPage="contact" />
    </div>
  );
}

export default ContactPage;
